import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Metric,
  ConnectorType,
  MetricsByConnectorType,
} from 'src/utils/gql/types';
import { formatNumberIT } from 'src/utils/stringUtils';

interface ChartData {
  date: string;
  value: number;
}

interface ChartWrapperProps {
  data: ChartData[];
  compareData?: ChartData[];
  formatValue: (value: number) => string;
  metric?: Metric;
  connectorType?: ConnectorType | string;
  dateRange?: { from: string; to: string };
  compareDateRange?: { from: string; to: string } | null;
}

// Tooltip custom per mostrare confronto con periodo precedente
const CustomTooltip = ({
  active,
  payload,
  label,
  compareData,
  formatValue,
  metric,
  connectorType,
  data,
  dateRange,
  compareDateRange,
}: any) => {
  if (!active || !payload || !payload[0]) return null;

  // Non assumere l'ordine del payload: può cambiare in base alla serie attiva
  const currentPayload =
    payload.find((p: any) => p?.dataKey === 'value') ?? payload[0];
  const comparePayload = payload.find(
    (p: any) => p?.dataKey === 'compareValue',
  );

  const currentValue = (currentPayload?.value ?? 0) as number;
  const currentDate = label as string;

  // Prendi il valore di comparazione direttamente dal payload (fonte di verità del punto hover)
  const compareValue =
    comparePayload && typeof comparePayload.value === 'number'
      ? (comparePayload.value as number)
      : null;

  // Calcola la data corrispondente nel periodo di confronto (date-only UTC-safe)
  let compareDateStr: string | null = null;
  if (compareDateRange && dateRange) {
    const parseYmd = (
      raw: string,
    ): { y: number; m: number; d: number } | null => {
      const match = String(raw || '')
        .slice(0, 10)
        .match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return null;
      return {
        y: Number(match[1]),
        m: Number(match[2]),
        d: Number(match[3]),
      };
    };

    const currentStartParts = parseYmd(dateRange.from);
    const currentEndParts = parseYmd(dateRange.to);
    const compareStartParts = parseYmd(compareDateRange.from);
    const compareEndParts = parseYmd(compareDateRange.to);
    const currentDateParts = parseYmd(currentDate);

    if (
      currentStartParts &&
      currentEndParts &&
      compareStartParts &&
      compareEndParts &&
      currentDateParts
    ) {
      const currentStart = Date.UTC(
        currentStartParts.y,
        currentStartParts.m - 1,
        currentStartParts.d,
      );
      const currentEnd = Date.UTC(
        currentEndParts.y,
        currentEndParts.m - 1,
        currentEndParts.d,
      );
      const compareStart = Date.UTC(
        compareStartParts.y,
        compareStartParts.m - 1,
        compareStartParts.d,
      );
      const compareEnd = Date.UTC(
        compareEndParts.y,
        compareEndParts.m - 1,
        compareEndParts.d,
      );
      const currentDateMs = Date.UTC(
        currentDateParts.y,
        currentDateParts.m - 1,
        currentDateParts.d,
      );

      const msDay = 1000 * 60 * 60 * 24;
      const currentDays = Math.floor((currentEnd - currentStart) / msDay);
      const compareDays = Math.floor((compareEnd - compareStart) / msDay);
      const dayIndex = Math.floor((currentDateMs - currentStart) / msDay);
      const normalizedIndex =
        compareDays > 0 && currentDays > 0
          ? (dayIndex / currentDays) * compareDays
          : 0;
      const compareDateMs = compareStart + Math.round(normalizedIndex) * msDay;
      const compareDate = new Date(compareDateMs);

      const compareYear = compareDate.getUTCFullYear();
      compareDateStr = compareDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: compareYear !== currentDateParts.y ? 'numeric' : undefined,
        timeZone: 'UTC',
      });
    }
  }

  // Calcola la percentuale di variazione se disponibile
  let changePercentage: number | null = null;
  if (
    compareValue !== null &&
    compareValue !== undefined &&
    compareValue !== 0
  ) {
    changePercentage = ((currentValue - compareValue) / compareValue) * 100;
  } else if (compareValue === 0 && currentValue > 0) {
    changePercentage = 100;
  } else if (compareValue === 0 && currentValue === 0) {
    changePercentage = 0;
  }

  // Metriche dove "più basso è meglio": logica invertita per colori e frecce
  // OAC, ACOS, ACOS_total, CAC: diminuzione = positivo (verde), aumento = negativo (rosso)
  const isInvertedMetric =
    connectorType === MetricsByConnectorType.KIPIFY &&
    metric?.key &&
    (metric.key === 'oac' ||
      metric.key === 'acos' ||
      metric.key === 'acos_total' ||
      metric.key === 'cac' ||
      metric.key === 'cac_new_customers');

  // Per metriche invertite: se percentage < 0 (diminuzione) è positivo, se percentage > 0 (aumento) è negativo
  const rawIsPositive = changePercentage !== null && changePercentage >= 0;
  const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;
  const formattedChangePercentage =
    changePercentage !== null
      ? `${isPositive ? '+' : '-'}${formatNumberIT(Math.abs(changePercentage), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`
      : null;

  // Formatta la data corrente per il display
  // Mostra l'anno se è diverso dall'anno del periodo principale (dateRange)
  const currentDateParts = String(currentDate || '')
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const currentYear = currentDateParts
    ? Number(currentDateParts[1])
    : new Date(currentDate).getFullYear();
  const dateRangeYear = dateRange
    ? Number(String(dateRange.from).slice(0, 4)) ||
      new Date(dateRange.from).getFullYear()
    : currentYear;

  // Mostra sempre l'anno se è diverso dall'anno del range principale, altrimenti mostra solo se diverso dall'anno corrente
  const systemYear = new Date().getFullYear();
  const shouldShowYear =
    currentYear !== dateRangeYear || currentYear !== systemYear;

  const currentDateFormatted = currentDateParts
    ? new Date(
        Number(currentDateParts[1]),
        Number(currentDateParts[2]) - 1,
        Number(currentDateParts[3]),
      ).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: shouldShowYear ? 'numeric' : undefined,
      })
    : new Date(currentDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: shouldShowYear ? 'numeric' : undefined,
      });

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        padding: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        minWidth: 200,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, mb: 1, color: '#000' }}
      >
        {currentDateFormatted}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: '#000' }}>
          <strong>Value:</strong> {formatValue(currentValue)}
        </Typography>
        {compareValue !== null && compareValue !== undefined && (
          <>
            <Typography variant="body2" sx={{ color: '#666' }}>
              <strong>Was:</strong> {formatValue(compareValue)}
              {compareDateStr && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: '#999', ml: 0.5, fontSize: '0.7rem' }}
                >
                  ({compareDateStr})
                </Typography>
              )}
            </Typography>
            {changePercentage !== null && (
              <Typography
                variant="body2"
                sx={{
                  color: isPositive ? '#22c55e' : '#ef4444',
                  fontWeight: 500,
                }}
              >
                <strong>{formattedChangePercentage}</strong>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

// Wrapper per i grafici che gestisce l'assenza di recharts
// e disegna un'area chart "da dashboard" in stile moderno
export const ChartWrapper: React.FC<ChartWrapperProps> = ({
  data,
  compareData,
  formatValue,
  metric,
  connectorType,
  dateRange,
  compareDateRange,
}) => {
  // Prova a importare recharts dinamicamente
  const [RechartsComponents, setRechartsComponents] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  // Prepara i dati combinati per il grafico quando c'è compareData
  // Allinea le date tra data corrente e compareData per mostrare entrambe le linee
  // IMPORTANTE: Questo hook deve essere chiamato PRIMA di qualsiasi return condizionale
  const chartDataToUse = React.useMemo(() => {
    if (!compareData || compareData.length === 0) {
      return data.map((d) => ({ ...d, compareValue: null }));
    }

    // Usa SOLO le date del periodo corrente come base.
    // Se il compare ha date extra/non allineate, non dobbiamo inventare punti correnti a 0.
    return data
      .map((currentPoint) => {
        const compareValue =
          compareData.find((d) => d.date === currentPoint.date)?.value ?? null;
        return {
          date: currentPoint.date,
          value: currentPoint.value,
          compareValue,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data, compareData]);

  React.useEffect(() => {
    const loadRecharts = async () => {
      try {
        const recharts = await import('recharts');
        setRechartsComponents({
          AreaChart: recharts.AreaChart,
          Area: recharts.Area,
          Line: recharts.Line,
          XAxis: recharts.XAxis,
          YAxis: recharts.YAxis,
          CartesianGrid: recharts.CartesianGrid,
          Tooltip: recharts.Tooltip,
          ResponsiveContainer: recharts.ResponsiveContainer,
        });
      } catch (error) {
        console.warn('Recharts not available:', error);
        setRechartsComponents(null);
      } finally {
        setLoading(false);
      }
    };

    loadRecharts();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">Loading chart...</Typography>
      </Box>
    );
  }

  if (!RechartsComponents || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">Chart not available</Typography>
      </Box>
    );
  }

  const {
    AreaChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
  } = RechartsComponents;

  // Se per qualche motivo una delle componenti di Recharts non è disponibile
  // evitiamo di renderizzare il grafico per non rompere React
  if (
    !AreaChart ||
    !Area ||
    !XAxis ||
    !YAxis ||
    !CartesianGrid ||
    !Tooltip ||
    !ResponsiveContainer
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">Chart not available</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight={200}>
      <AreaChart
        data={chartDataToUse}
        margin={{ top: 8, right: 8, bottom: 4, left: -12 }}
      >
        <defs>
          <linearGradient id="kpiAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="date"
          stroke="#666"
          fontSize={12}
          // Lasciamo che Recharts decida quanti tick mostrare
          // ma formattiamo sempre per giorno/mese (es. "Dec 3")
          minTickGap={20}
          tickFormatter={(value: string | number) => {
            // value è già in formato YYYY-MM-DD
            if (typeof value === 'string') {
              const parts = value.split('-');
              if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                const day = parseInt(parts[2], 10);
                if (
                  !Number.isNaN(year) &&
                  !Number.isNaN(month) &&
                  !Number.isNaN(day)
                ) {
                  const d = new Date(year, month - 1, day);
                  return d.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                }
              }
            }
            const date = new Date(value);
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
          }}
        />
        <YAxis stroke="#666" fontSize={12} />
        <Tooltip
          content={
            <CustomTooltip
              compareData={compareData}
              formatValue={formatValue}
              metric={metric}
              connectorType={connectorType}
              data={chartDataToUse}
              dateRange={dateRange}
              compareDateRange={compareDateRange}
            />
          }
        />
        {/* Linea dell'anno precedente - mostrata solo se compareData è disponibile */}
        {compareData && compareData.length > 0 && Line && (
          <Line
            type="monotone"
            dataKey="compareValue"
            stroke="#94a3b8"
            strokeWidth={1.5}
            strokeDasharray="5 5"
            dot={false}
            activeDot={{ r: 4, fill: '#94a3b8' }}
            // Evita "buchi" visivi quando il confronto non ha punti
            // per alcune date: colleghiamo i segmenti adiacenti.
            connectNulls={true}
            isAnimationActive={false}
          />
        )}
        <Area
          type="monotone"
          dataKey="value"
          stroke="#22c55e"
          strokeWidth={2}
          fill="url(#kpiAreaGradient)"
          dot={{ r: 2 }}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
